/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';

import {
    ApplicationState, UserReducer, NotificationReducer, StorageOperations,
    ApiReducer, Api, OrganizationMetadataReducer
} from '../../data/interfaces';
import { fetchFeaturedApiListAuthenticated } from '../../data/operations';
import {
    RESOURCE_API,
    HOME_PAGE_API_QUERY,
    RETURN_TO_PATH,
    SELECTED_ORG_UUID,
    ORG_QUERY_PARAM,
    FIDP,
    RESOURCE_SIGN_IN,
    PATH_SEPARATOR,
    CORRELATION_ID,
    CORRELATION_ID_QUERY_PARAM,
    RESOURCE_CLIENT_ERROR, SELECTED_ORG_HANDLE_NAME
} from '../../data/constants';
import { sessionStoreOperation, localStoreOperation, isValidData, getAPIType } from '../../data/utils';
import DevPortalFooter from '../shared/DevPortalFooter';
import { Avatar, Box, Button, Container, Grid, Typography, useTheme, CssBaseline } from '@material-ui/core';
import GreyOutlinedButton from '../../elements/GreyOutlinedButton';
import ApiCard from '../api-list/ApiCard';
import { useStyles } from './styles';
import { trackBrowseAPIButtonCLick, trackHomePageLoad } from '../../util/app-insights';

export interface ApiProps {
    apiReducer: ApiReducer,
    userReducer: UserReducer,
    organizationMetadataReducer: OrganizationMetadataReducer,
    notificationReducer: NotificationReducer,
    fetchFeaturedApiListAuthenticated: (organizationId: string, pagination?: string) => Promise<void>
}

function DevportalHome({ apiReducer, userReducer, organizationMetadataReducer, fetchFeaturedApiListAuthenticated }: ApiProps) {
    const classes = useStyles();
    const theme = useTheme();
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const { orgName } = useParams<{orgName: string}>();
    const { apimTokenObtained, orgs: userOrganizations, selectedOrg, signedIn } = userReducer;
    const { featuredApiListLoading: apiLoading, featuredApiList, thumbnailMap } = apiReducer;
    const [apiListState, setApiListState] = useState<{ apiList: Api[], thumbnailMap: { [key: string]: string } }>
        ({ apiList: featuredApiList.list, thumbnailMap: thumbnailMap });
    const apiListQuery = theme.custom.homePage.sections.featuredApis.apiListQuery ?? HOME_PAGE_API_QUERY;
    const browseApisHandleClick = () => {
        trackBrowseAPIButtonCLick();
        history.push(PATH_SEPARATOR + orgName + RESOURCE_API);
    }

    const checkUserAccessToCurrentPathOrg = (): boolean => {
        const selectedOrganization = userOrganizations.find(userOrg => userOrg.handle === orgName);
        return !!selectedOrganization;
    }

    const hasUserAccessToCurrentPathOrg = checkUserAccessToCurrentPathOrg();

    useEffect(() => {
        if (hasUserAccessToCurrentPathOrg) {
            trackHomePageLoad(localStoreOperation(StorageOperations.GET, CORRELATION_ID) || '');
        }
    }, [hasUserAccessToCurrentPathOrg]);

    useEffect(() => {
      if (signedIn && orgName && !hasUserAccessToCurrentPathOrg) {
        history.push("/forbidden");
      }
    }, [selectedOrg]);

    const sectionsOrder: string[] = theme.custom.homePage.sections.displayOrder ??
        ["IntroVideo", "FeaturedApis", "OrganizationInfo", "UseCases", "DeveloperBlogs"];

    const IntroVideo = () => (
        theme.custom.homePage.sections.introVideo.enabled ?
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} key={"IntroVideo"}>
                <Container style={{ maxWidth: '1640px', borderRadius: '8px' }}>
                    <Typography component="div" className={classes.introVideoTitle}>
                        {
                            theme.custom.homePage.sections.introVideo.title.text ??
                            intl.formatMessage({
                                id: "home.sections.introVideo.title",
                                defaultMessage: "Getting Started with WSO2 Developer Platform APIs"
                            })
                        }
                    </Typography>
                    <Typography component="div" className={classes.introVideoSubTitle}>
                        {
                            theme.custom.homePage.sections.introVideo.subTitle.text ??
                            intl.formatMessage({
                                id: "home.sections.introVideo.subTitle",
                                defaultMessage: "Build the future with us. Our APIs enable you to automate, "
                                    + "innovate, and connect to millions of WSO2 Developer Platform customers"
                            })
                        }
                    </Typography>
                    <Grid container>
                        <Grid item xl={12} lg={12} md={12} sm={12} xs={12} hidden>
                            <iframe className={classes.iframe}
                                src={
                                    theme.custom.homePage.sections.introVideo.videoUrl ??
                                    "https://www.youtube.com/embed/pax5EJFM2mM"
                                }
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen={true}
                                id="home-video"
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Grid>
            : null
    );

    const FeaturedApis = () => (
        theme.custom.homePage.sections.featuredApis.enabled && apimTokenObtained ?
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} key={"FeaturedApis"}>
                <Typography component="div" className={classes.featuredTitle}>
                    {
                        theme.custom.homePage.sections.featuredApis.title.text ??
                        intl.formatMessage({
                            id: "home.sections.featuredApis.title",
                            defaultMessage: "Featured APIs"
                        })
                    }
                </Typography>
                <Typography component="div" className={classes.featuredSubTitle}>
                    {
                        theme.custom.homePage.sections.featuredApis.subTitle.text ??
                        intl.formatMessage({
                            id: "home.sections.featuredApis.subTitle",
                            defaultMessage: "View Featured WSO2 Developer Platform APIs or browse to find the APIs that meet your specific needs"
                        })
                    }
                </Typography>
                <Container style={{ maxWidth: '1720px' }}>
                    <Grid container style={{ justifyContent: 'center' }}>
                        {
                            apiLoading ?
                                [...Array(3).keys()].map((key) => (
                                    <Grid item key={key} xl={3} lg={3} md={4} sm={6} xs={12}>
                                        <ApiCard
                                            apiId={""}
                                            apiVersion={""}
                                            apiTitle={""}
                                            apiImageUrl={""}
                                            apiRating={0}
                                            apiSummary={""}
                                            organizationHandle={orgName}
                                            userReducer={userReducer}
                                        />
                                    </Grid>
                                ))
                                :
                                apiListState.apiList?.length > 0 ?
                                    apiListState.apiList?.map((api) => (
                                        <Grid item key={api.id} xl={3} lg={3} md={4} sm={6} xs={12}>
                                            <ApiCard
                                                apiId={api.id}
                                                apiVersion={api.version}
                                                apiTitle={api.name}
                                                apiImageUrl={apiListState.thumbnailMap[api.id]}
                                                apiRating={parseFloat(api.avgRating)}
                                                apiSummary={api.description}
                                                organizationHandle={orgName}
                                                userReducer={userReducer}
                                                apiType={getAPIType(api)}
                                            />
                                        </Grid>
                                    ))
                                    :
                                    (
                                        <Grid item xl={3} lg={3} md={4} sm={6} xs={12}>
                                            <img src={theme.custom.homePage.sections.featuredApis.noApisImage.src ??
                                                'themes/default/images/no-apis.svg'}
                                            />
                                            <Typography component="div" className={classes.featuredSubTitle}>
                                                <FormattedMessage
                                                    id="home.sections.featuredApis.messages.noApisMessage"
                                                    defaultMessage="You do not have any featured APIs available"
                                                />
                                            </Typography>
                                        </Grid>
                                    )
                        }
                    </Grid>
                </Container>
                <GreyOutlinedButton
                    text={
                        theme.custom.homePage.sections.featuredApis.browseButton.text ??
                        intl.formatMessage({
                            id: "home.sections.featuredApis.buttons.browseAllApis",
                            defaultMessage: "Browse all APIs"
                        })
                    }
                    onClick={browseApisHandleClick} className={classes.browseButtonFeatured}
                />
            </Grid>
            : null
    );

    const OrganizationInfo = () => (
        theme.custom.homePage.sections.organization.enabled ?
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} key={"OrganizationInfo"}>
                <Container className={classes.organization}>
                    <Typography component="div" className={classes.organizationTitle}>
                        {
                            theme.custom.homePage.sections.organization.title.text ??
                            intl.formatMessage({
                                id: "home.sections.organization.title",
                                defaultMessage: "Why WSO2 Developer Platform"
                            })
                        }
                    </Typography>
                    <Typography component="div" className={classes.organizationSubTitle}>
                        {
                            theme.custom.homePage.sections.organization.subTitle.text ??
                            intl.formatMessage({
                                id: "home.sections.organization.subTitle",
                                defaultMessage: "Build the future with us. Our APIs enable you to automate, "
                                    + "innovate, and connect to millions of WSO2 Developer Platform customers"
                            })
                        }
                    </Typography>
                    <Grid container>
                        {
                            theme.custom.homePage.sections.organization.cards.map((card: any) => (
                                <Grid item key={card.title} xl={4} lg={4} md={6} sm={12} xs={12}>
                                    <Box style={{
                                        maxWidth: '339px',
                                        marginTop: '60px',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        marginBottom: '25px'
                                    }}
                                    >
                                        <Avatar src={card.icon} variant="square" className={classes.organizationCardIcon} />
                                        <Typography className={classes.organizationCardTitle}>
                                            {card.title}
                                        </Typography>
                                        <Typography className={classes.organizationCardContent}>
                                            {card.content}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))
                        }
                    </Grid>
                </Container>
            </Grid>
            : null
    );

    const UseCases = () => (
        theme.custom.homePage.sections.useCases.enabled ?
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} key={"UseCases"}>
                <Container className={classes.useCases}>
                    <Typography component="div" className={classes.useCasesTitle}>
                        {
                            theme.custom.homePage.sections.useCases.title.text ??
                            intl.formatMessage({
                                id: "home.sections.useCases.title",
                                defaultMessage: "Use Cases"
                            })
                        }
                    </Typography>
                    <Typography component="div" className={classes.useCasesSubTitle}>
                        {
                            theme.custom.homePage.sections.useCases.subTitle.text ??
                            intl.formatMessage({
                                id: "home.sections.useCases.subTitle",
                                defaultMessage: "Need inspiration? Check out real-world scenarios for using our APIs. "
                                    + "The page will contain basic information about the organization and its API services"
                            })
                        }
                    </Typography>
                    <Grid container>
                        {
                            theme.custom.homePage.sections.useCases.cards.map((card: any) => (
                                <Grid item key={card.title} xl={3} lg={3} md={4} sm={6} xs={12}>
                                    <Box style={{
                                        maxWidth: '228px',
                                        marginTop: '60px',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        marginBottom: '25px'
                                    }}
                                    >
                                        <Avatar src={card.icon} variant="square" className={classes.useCasesCardIcon} />
                                        <Typography className={classes.useCasesCardTitle}>
                                            {card.title}
                                        </Typography>
                                        <Typography className={classes.useCasesCardContent}>
                                            {card.content}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))
                        }
                    </Grid>
                </Container>
            </Grid>
            : null
    );

    const DeveloperBlog = () => (
        theme.custom.homePage.sections.developerBlog.enabled ?
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} key={"DeveloperBlogs"}>
                <Container className={classes.devBlogs}>
                    <Typography component="div" className={classes.blogTitle}>
                        {
                            theme.custom.homePage.sections.developerBlog.title.text ??
                            intl.formatMessage({
                                id: "home.sections.developerBlog.title",
                                defaultMessage: "Developer Blog"
                            })
                        }
                    </Typography>
                    <Typography component="div" className={classes.blogSubTitle}>
                        {
                            theme.custom.homePage.sections.developerBlog.subTitle.text ??
                            intl.formatMessage({
                                id: "home.sections.developerBlog.subTitle",
                                defaultMessage: "Our developer blog is a place for all business and IT developers"
                            })
                        }
                    </Typography>
                    <Grid container>
                        {
                            theme.custom.homePage.sections.developerBlog.cards.map((card: Record<string, string>) => (
                                <Grid item key={card.title} xl={6} lg={6} md={6} sm={12} xs={12}>
                                    <Box style={{
                                        maxWidth: '576px',
                                        marginTop: '60px',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        marginBottom: '25px'
                                    }}
                                    >
                                        <Typography className={classes.blogCardTitle}>
                                            {card.title}
                                        </Typography>
                                        <Typography className={classes.blogCardAuthor}>
                                            {card.author}
                                        </Typography>
                                        <Typography className={classes.blogCardContent}>
                                            {card.content}
                                        </Typography>
                                        <Box style={{ textAlign: 'left' }}>
                                            <Button variant="text"
                                                className={classes.blogReadMoreBtn}
                                                href={card.blogUrl}
                                                target={"_blank"}
                                            >
                                                <FormattedMessage
                                                    id="home.sections.developerBlog.cards.button.readMore"
                                                    defaultMessage="Read More"
                                                />
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))
                        }
                    </Grid>
                </Container>
            </Grid>
            : null
    );

    const homePageSectionsMap: { [key: string]: JSX.Element | null } = {
        "IntroVideo": IntroVideo(),
        "FeaturedApis": FeaturedApis(),
        "OrganizationInfo": OrganizationInfo(),
        "UseCases": UseCases(),
        "DeveloperBlogs": DeveloperBlog()
    };

    useEffect(() => {
        if (!apimTokenObtained) {
            if (!organizationMetadataReducer.success && organizationMetadataReducer.error.message
                && organizationMetadataReducer.error.message.includes("Request failed with status code 404")) {
                    history.push(RESOURCE_CLIENT_ERROR);
            }

            sessionStoreOperation(StorageOperations.ADD, RETURN_TO_PATH, window.location.pathname);
            const queryParam = new URLSearchParams(location.search);
            const orgUuid = queryParam.get(ORG_QUERY_PARAM);
            const correlationId = queryParam.get(CORRELATION_ID_QUERY_PARAM);
            if (orgUuid) {
                localStoreOperation(StorageOperations.ADD, SELECTED_ORG_UUID, orgUuid);
            }
            if (correlationId) {
                localStoreOperation(StorageOperations.ADD, CORRELATION_ID, correlationId);
            }
            // Checking whether request is from choreo portal and setting fidp value
            if (queryParam.get('fidp')) {
                localStoreOperation(StorageOperations.ADD, FIDP, queryParam.get('fidp') ?? "");
                history.push(RESOURCE_SIGN_IN);
            } else if (localStoreOperation(StorageOperations.GET, FIDP) && orgName == localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME)) {
                // Redirecting user to sigin if fidp value already exists in local store and orghandle in url and local store are equivalent
                history.push(RESOURCE_SIGN_IN);
            }
        } else if (apimTokenObtained && selectedOrg.uuid && hasUserAccessToCurrentPathOrg) {
            fetchFeaturedApiListAuthenticated(selectedOrg.uuid, apiListQuery);
        }
    }, [selectedOrg]);

    useEffect(() => {
        if (featuredApiList) {
            setApiListState((prevState) => {
                return {
                    ...prevState,
                    apiList: featuredApiList.list
                }
            });
        }
    }, [featuredApiList]);

    useEffect(() => {
        if (isValidData(thumbnailMap)) {
            setApiListState((prevState) => {
                return {
                    ...prevState,
                    thumbnailMap: thumbnailMap
                }
            });
        }
    }, [thumbnailMap]);

    return (
        <div className={classes.root}>
            <CssBaseline />
            <main className={classes.content}>
                        <div className={classes.appBarSpacer} />
                        <Container maxWidth="xl">
                            <Grid container>
                                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                                    <Grid container>
                                        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                            <Box className={classes.homeTitleBox}>
                                                <Typography component="div" className={classes.homeTitle} data-testid="home-page-banner-title">
                                                    {
                                                        theme.custom.homePage.title.text ??
                                                        intl.formatMessage({
                                                            id: "home.banner.title",
                                                            defaultMessage: "Continue working with APIs"
                                                        })
                                                    }
                                                </Typography>
                                                <Typography component="div" className={classes.homeSubTitle}>
                                                    {
                                                        theme.custom.homePage.subTitle.text ??
                                                        intl.formatMessage({
                                                            id: "home.banner.subTitle",
                                                            defaultMessage: "Use our APIs to automate, innovate, and connect to millions of customers"
                                                        })
                                                    }
                                                </Typography>
                                            </Box>
                                            <Box className={classes.homeTitleBox}>
                                                <div className={classes.homeTitleButtons}>
                                                    {/* Disabled until anonymous view is enabled */}
                                                    {/* <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className={classes.signUpButton}
                                                    onClick={browseApisHandleClick}
                                                >
                                                    Sign Up
                                                </Button> */}
                                                    <Button variant="contained" color="primary"
                                                            className={classes.browseButton}
                                                            onClick={browseApisHandleClick}
                                                    >
                                                        {theme.custom.homePage.browseButton.text ??
                                                        intl.formatMessage({
                                                            id: "home.banner.buttons.browseButton",
                                                            defaultMessage: "Browse APIs"
                                                        })
                                                        }
                                                    </Button>
                                                </div>
                                            </Box>
                                        </Grid>
                                        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                                            <Box>
                                                <img
                                                    src={theme.custom.homePage.banner.src ?? "themes/default/images/banner.svg"}
                                                    className={classes.homeBanner}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {
                                    sectionsOrder.map((section: string) => (
                                        homePageSectionsMap[section]
                                    ))
                                }
                            </Grid>
                        </Container>
                    </main>
            <DevPortalFooter />
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        userReducer: state.userReducer,
        notificationReducer: state.notificationReducer,
        organizationMetadataReducer: state.organizationMetadataReducer,
    }
}

const mapDispatchToProps = {
    fetchFeaturedApiListAuthenticated: fetchFeaturedApiListAuthenticated
}

export default connect(mapStateToProps, mapDispatchToProps)(DevportalHome);
